import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  UsersPage, PageHeader, PageTitle, UsersFilters, SearchBox, SearchInput,
  SearchButton, UsersTable, TableHeader, TableRow,
  UserAvatar, UserName, UserEmail, JoinDate, UserActions,
  ActionButton, UsersStats, StatItem, StatNumber, StatLabel
} from '../../styles/UsersStyles';
import { UsersList } from '../../common/CommonApi';

const PAGE_SIZE = 30;

const USERS_TABLE_COLUMNS = '0.6fr 1.3fr 1.1fr 1.6fr 0.8fr 1fr 1fr 0.8fr 0.8fr';

const UsersTableHeader = styled(TableHeader)`
  grid-template-columns: ${USERS_TABLE_COLUMNS};
  min-width: 920px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UsersTableRow = styled(TableRow)`
  grid-template-columns: ${USERS_TABLE_COLUMNS};
  min-width: 920px;

  @media (max-width: 768px) {
    min-width: 0;
    grid-template-columns: 1fr 1fr;
    margin: 10px;
    padding: 14px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;

    > * {
      min-width: 0;
      overflow-wrap: anywhere;
    }

    > :nth-child(n + 2)::before {
      display: block;
      margin-bottom: 4px;
      color: #94a3b8;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
    }

    > :nth-child(2)::before { content: "Name"; }
    > :nth-child(3)::before { content: "Phone"; }
    > :nth-child(4)::before { content: "Email"; }
    > :nth-child(5)::before { content: "Gender"; }
    > :nth-child(6)::before { content: "Refer code"; }
    > :nth-child(7)::before { content: "Joined"; }
    > :nth-child(8)::before { content: "Status"; }
    > :nth-child(9)::before { content: "Actions"; }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const formatJoinDate = (user) => {
  const value = user.createdAt || user.joinDate;
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchUsers = async (page = 1) => {
    setLoading(true);
    try {
      const response = await UsersList(page, PAGE_SIZE);
      const data = response?.data?.data || {};
      const userList = Array.isArray(data) ? data : data.users || [];
      setUsers(userList);
      setPagination(data.pagination || { page, pages: 1, total: userList.length });
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const visibleUsers = users.filter((user) => {
    const query = searchTerm.toLowerCase();
    return !query || [user.phoneNumber, user.email, user.name, user.refer_code]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(query));
  });

  return (
    <UsersPage>
      <PageHeader><PageTitle>Users Management</PageTitle></PageHeader>
      <UsersFilters>
        <SearchBox>
          <SearchInput type="text" placeholder="Search users on this page..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <SearchButton type="button">🔍</SearchButton>
        </SearchBox>
      </UsersFilters>
      <UsersStats style={{ marginBottom: '20px' }}>
        <StatItem><StatNumber>{pagination.total}</StatNumber><StatLabel>Total Users</StatLabel></StatItem>
        <StatItem><StatNumber>{users.filter((user) => !user.isDeleted).length}</StatNumber><StatLabel>Active on page</StatLabel></StatItem>
        <StatItem><StatNumber>{users.filter((user) => user.refer_code).length}</StatNumber><StatLabel>Referred Users on page</StatLabel></StatItem>
      </UsersStats>
      <UsersTable>
        <UsersTableHeader>
          <div>Profile</div><div>Name</div><div>Phone Number</div><div>Email</div>
          <div>Gender</div><div>Refer Code</div><div>Join Date</div><div>Status</div><div>Actions</div>
        </UsersTableHeader>
        {loading ? <EmptyState>Loading users...</EmptyState> : visibleUsers.map((user) => (
          <UsersTableRow key={user._id}>
            <UserAvatar>👤</UserAvatar>
            <UserName>{user.name || '—'}</UserName>
            <div>{user.phoneNumber || '—'}</div>
            <UserEmail>{user.email || '—'}</UserEmail>
            <div style={{ textTransform: 'capitalize' }}>{user.gender || '—'}</div>
            <div>{user.refer_code || '—'}</div>
            <JoinDate>{formatJoinDate(user)}</JoinDate>
            <div>{user.isDeleted ? 'Inactive' : 'Active'}</div>
            <UserActions><ActionButton className="view" title="View user">👁️</ActionButton></UserActions>
          </UsersTableRow>
        ))}
        {!loading && !visibleUsers.length && <EmptyState>No users found.</EmptyState>}
      </UsersTable>
      <PaginationBar>
        <span>Page {pagination.page || 1} of {pagination.pages || 1} · {pagination.total} users</span>
        <div>
          <button type="button" disabled={loading || pagination.page <= 1} onClick={() => fetchUsers(pagination.page - 1)}>← Previous</button>
          <button type="button" disabled={loading || !pagination.pages || pagination.page >= pagination.pages} onClick={() => fetchUsers(pagination.page + 1)}>Next →</button>
        </div>
      </PaginationBar>
    </UsersPage>
  );
};

const EmptyState = styled.div`padding: 28px; text-align: center; color: #64748b;`;
const PaginationBar = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 20px; color: #64748b;
  button { background: #fff; border: 1px solid #cbd5e1; border-radius: 6px; cursor: pointer; margin-left: 8px; padding: 9px 13px; }
  button:disabled { cursor: not-allowed; opacity: .5; }

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;

    div {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    button { margin-left: 0; }
  }
`;

export default Users;
